import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/header";
import SelectedAnswersContainer from "../components/AnswerContainer/SelectedAnswersContainer";
import CareerWheel from "../components/CareerWheel";
import Question from "../components/QuestionModal/Question";
import HelpModal from "../components/HelpModal";

import { useDispatch, useSelector } from "react-redux";
import {
  toggleCareerModal,
  toggleQuestionPopup,
  setCareerData,
  fetchNextQuestion,
  fetchResults,
  recordProfessionClick,
} from "../slices/dataSlice";
import CareerDialogueModal from "../components/CareerDialogueModal";
import Loader from "../components/loader";
import ResultsPageHeader from "../components/ResultContainer/ResultPageHeader";
import { retranslate } from "../lib/googleTranslate";

const ResultsPage = () => {
  const {
    resultsAPI,
    order,
    recommendations,
    showCareerModal,
    isQuestionPoupVisible,
    newQuestionAPI,
    careerData,
    freezeScreen,
  } = useSelector((state) => state.data);

  const [categories, setCategories] = useState([]);
  const [sRange, setSRange] = useState([27, 250]);
  const [yRange, setYRange] = useState([1, 12]);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resultsAbortControllerRef = useRef(null);

  useEffect(() => {
    return () => resultsAbortControllerRef.current?.abort();
  }, []);

  useEffect(() => {
    const getNextQuestion = async () => {
      if (
        newQuestionAPI.status === "failed" ||
        newQuestionAPI.status === "loading"
      ) {
        try {
          const q = await dispatch(fetchNextQuestion());
          if (fetchNextQuestion.fulfilled.match(q) && q.payload) {
            dispatch(toggleQuestionPopup(true));
          }
        } catch (error) {
          console.error("Failed to recover question:", error);
        }
      }
    };

    getNextQuestion();
  }, [dispatch, resultsAPI.status]);

  // Cancel results API when freezeScreen becomes true
  useEffect(() => {
    if (freezeScreen && resultsAbortControllerRef.current) {
      resultsAbortControllerRef.current.abort();
      resultsAbortControllerRef.current = null;
    }
  }, [freezeScreen]);

  useEffect(() => {
    if (recommendations?.category) {
      setCategories(recommendations.category);
    }
  }, [recommendations]);

  // Clusters are generated well after load, so on anything but a fast
  // connection they render after Google has finished translating the page and
  // stay in English. Ask for a fresh pass once they are on screen.
  useEffect(() => {
    if (categories.length) retranslate();
  }, [categories]);

  const filteredCategories = useMemo(() => {
    if (!categories.length) return [];

    const [minSalaryK, maxSalaryK] = sRange;
    const minSalary = minSalaryK * 1000;
    const maxSalary = maxSalaryK * 1000;

    const [minYears, maxYears] = yRange;

    return categories
      .map((category) => {
        const eligibleJobs = category.data.filter((job) => {
          const years = Number(job.years_of_study);

          const salaryMatches =
            job.min_salary <= maxSalary && job.max_salary >= minSalary;

          const yearsMatches = years >= minYears && years <= maxYears;

          return salaryMatches && yearsMatches;
        });

        if (!eligibleJobs.length) return null;

        const sortedByRank = [...eligibleJobs].sort(
          (a, b) => a.cluster_rank - b.cluster_rank,
        );

        const visibleJobs = sortedByRank.slice(0, 5);
        const hiddenJobs = sortedByRank.slice(5);

        return {
          ...category,
          visibleJobs,
          hiddenJobs,
          totalJobs: sortedByRank.length,
        };
      })
      .filter(Boolean);
  }, [categories, sRange, yRange]);

  /*const filteredCategories = useMemo(() => {
  if (!categories.length) return [];

  // slider values like [97, 250] → $97k–$250k
  const [minSalaryK, maxSalaryK] = sRange;
  const minSalary = minSalaryK * 1000;
  const maxSalary = maxSalaryK * 1000;

  const [minYears, maxYears] = yRange;

  return categories
    .map(category => {
      const filteredData = category.data.filter(item => {
        const years = Number(item.years_of_study);

        return (
          item.min_salary <= maxSalary &&
          item.max_salary >= minSalary &&
          years >= minYears &&
          years <= maxYears
        );
      });

      return {
        ...category,
        data: filteredData,
      };
    })
    .filter(category => category.data.length > 0);
}, [categories, sRange, yRange]);*/

  const handleClickMoreQuestions = async () => {
    let q = await dispatch(fetchNextQuestion());
    if (q.payload) {
      dispatch(toggleQuestionPopup(true));
    }
  };

  const handleCareerClick = (career) => {
    dispatch(setCareerData(career));
    dispatch(toggleCareerModal(true));
    dispatch(recordProfessionClick(career));
  };

  const handleSalaryChange = (value) => {
    setSRange(value);
  };

  const handleYearChange = (value) => {
    setYRange(value);
  };

  const resultsReady =
    resultsAPI.status === "succeeded" && recommendations?.category?.length > 0;

  const isProcessing = resultsAPI.status === "loading";
  const hasResultsError = resultsAPI.status === "failed" && !resultsReady;

  const handleRetryResults = () => {
    if (freezeScreen) return;
    resultsAbortControllerRef.current?.abort();
    resultsAbortControllerRef.current = dispatch(fetchResults());
  };

  return (
    <div className="min-h-screen flex flex-col">
      {freezeScreen && <HelpModal isOpen={freezeScreen} />}

      <CareerDialogueModal showCareer={showCareerModal} content={careerData} />

      {!freezeScreen && <Question modalOpen={isQuestionPoupVisible} />}

      <Header logoOnly={true} />

      <main className="flex-1">
        <h1 className="sr-only">Career Results and Recommendations</h1>
        <SelectedAnswersContainer handleViewMore={handleClickMoreQuestions} />

        {resultsReady ? (
          <ResultsPageHeader
            summmary={recommendations?.final_summary}
            onSalaryChange={handleSalaryChange}
            onYearChange={handleYearChange}
          />
        ) : null}

        {isProcessing && <Loader className="p-10" aria-live="polite" />}

        {hasResultsError && (
          <div
            className="mx-auto my-8 max-w-2xl rounded-lg border border-red-200 bg-red-50 p-6 text-center"
            role="alert"
            aria-live="assertive"
          >
            <p className="text-base font-semibold text-red-700">
              We could not load your results.
            </p>
            <p className="mt-2 text-sm text-red-600">
              {resultsAPI?.error?.detail ||
                resultsAPI?.error?.message ||
                "Please try again."}
            </p>
            <button
              type="button"
              onClick={handleRetryResults}
              className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {resultsReady && filteredCategories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 px-2">
            {filteredCategories.map((wheel, index) => (
              <div
                key={wheel.category}
                className={`min-w-[460px] ${
                  index >= 3 ? "flex-basis-full flex justify-center" : ""
                }`}
              >
                <CareerWheel
                  label={wheel.label}
                  onCareerClick={handleCareerClick}
                  data={wheel.visibleJobs}
                />
              </div>
            ))}
          </div>
        )}

        {resultsReady && filteredCategories.length === 0 && (
          <div
            className="flex-1 flex flex-col items-center justify-center p-10 text-center text-border-bold"
            role="status"
            aria-live="polite"
          >
            <p className="text-lg font-medium">
              Please adjust the selection criteria,
            </p>
            <p className="text-sm">
              no professions qualified based on your filters
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ResultsPage;
